<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'

  import { BellIcon } from '@heroicons/vue/24/outline'
  import HeaderSearch from '../global-search/HeaderSearch.vue'
  import { useAuthStore } from '@/stores'
  // import { EvaluateRouteSecurity, PiniaResettable, Router } from '@/helpers'
  import { useColorMode, useCycleList, useLocalStorage } from '@vueuse/core'
  import { MenuCloseIcon, RoleAdminIcon, RoleBetaIcon, RoleDevIcon, RoleManagementIcon } from '@/config/icons'
  import Menu from 'primevue/menu'
  import type { RouteRecordRaw } from 'vue-router'
  import { useRouter } from 'vue-router'
  import { useToast } from 'primevue/usetoast'
  import { EvaluateRouteSecurity, Router } from '@/helpers/router'
  import NotificationHeaderComponent from '@/components/layout/NotificationHeaderComponent.vue'
  import { useTitrop } from '@/composables/useTitrop.ts'
  import { cacheDB } from '@/plugins/storeCachePlugin.ts'
  import { useTitropEvents } from '@/composables/useTitropEvents.ts'
  import ButtonDefault from '@/elements/ButtonDefault.vue'
  import NavigationMobile from '@/components/layout/NavigationMobile.vue'

  const isLightMode = computed(() => newStyle.value == 'light')
  const isDarkMode = computed(() => newStyle.value == 'dark')
  const isAutoMode = computed(() => newStyle.value == 'auto')

  const titrop = useTitrop()
  const router = useRouter()
  const currentRoute = computed(() => router.currentRoute.value.fullPath)


  function toggleNotifications() {
    useTitropEvents().hotkeyToggleActivityFeed.emit()
  }

  function toggleMetric() {
    // TODO: unfinished feature
  }

  function toggleNavigation() {
    //const storage = useLocalStorage<boolean>('navigation-visible', true, { writeDefaults: true })
    isMenuVisible.value = !isMenuVisible.value
  }

  const isMenuVisible = useLocalStorage<boolean>('navigation-visible', true, { writeDefaults: true })

  const authStore = useAuthStore()
  const usermeta = authStore.whoami
  const avatarUrl = usermeta?.avatarUrl.widget_profile_avatar ?? undefined

  function doLogout() {
    void authStore.logout()
  }

  const mode = useColorMode({ initialValue: 'light', storageKey: null })
  const { next: nextStyle, state: newStyle } = useCycleList<'light' | 'dark' | 'auto'>(['auto', 'dark', 'light'], { initialValue: 'light' })
  watch(newStyle, (m) => (mode.value = m))

  const headerMenu = ref<InstanceType<typeof Menu>>()
  const headerMenuOpen = ref(false)

  function toggleHeaderMenu(event: Event) {
    headerMenu.value?.toggle(event)
    headerMenuOpen.value = !headerMenuOpen.value
  }

  // const headerMenuItems = ref([
  //     {
  //         label: 'Options',
  //         items: [
  //             {
  //                 label: 'Refresh',
  //                 icon: 'pi pi-refresh'
  //             },
  //             {
  //                 label: 'Export',
  //                 icon: 'pi pi-upload'
  //             }
  //         ]
  //     }
  // ])
  interface MenuItem {
    label: string
    routeOrFunc: RouteRecordRaw | ((event?: Event) => void)
    type?: 'link'
    icon?: string
  }

  const headerMenuItems = ref<MenuItem[]>([]) // <MenuItem
  const navigation = ref<RouteRecordRaw[]>(Router.options.routes.find((r) => r.name === '__header_navigation__')!.children!)

  if (navigation.value) {
    for (const route of navigation.value) {
      if (!EvaluateRouteSecurity(route)) continue
      const label = route.meta?.title ? route.meta.title : 'Missing .title @ ' + String(route.name ?? route.path)
      headerMenuItems.value.push({
        label: label,
        routeOrFunc: route,
        icon: route.icon
      })
    }
  }
  // headerMenuItems.value.push({
  //     label: 'Toggle DevDumps',
  //     type: 'switch',
  //     value: valueRef,
  //     onLabel: 'an',
  //     offLabel: 'aus'
  // })
  // }

  headerMenuItems.value.push({ label: 'Abmelden', routeOrFunc: doLogout, icon: 'LogoutIcon' })

  const markAsDevClasses = 'bg-blue-300 hover:bg-blue-400 mb-0.5'
  const markAsBetaClasses = 'bg-green-300 hover:bg-green-400 mb-0.5'
  const markAsAdminClasses = 'bg-red-300 hover:bg-red-400 mb-0.5'

  const toastService = useToast()

  async function clearStoreCaches() {
    //;(getActivePinia() as PiniaResettable).$clearCaches() //OLD WAY
    await cacheDB.delete()
    toastService.add({
      summary: 'Alle Caches gelöscht',
      life: 2500,
      severity: 'success'
    })
    location.reload()
  }

  const onMenuHide = () => {
    headerMenuOpen.value = false
  }

  const test = (item: MenuItem) => {
    if (typeof item.routeOrFunc === 'function') {
      item.routeOrFunc()
    } else {
      router.push(item.routeOrFunc)
    }
  }
</script>

<template>
  <div
    id="header"
    class="flex w-full flex-wrap md:flex-nowrap flex-1 gap-x-4 gap-y-1 md:gap-y-0 self-stretch">
    <!-- Desktop nav button -->
    <div class="hidden md:order-1 md:block">
      <ButtonDefault
        @click="toggleNavigation"
        outlined
        :icon="isMenuVisible ? 'MenuOpenIcon' : 'MenuCloseIcon'"
        severity="secondary" />
    </div>

    <ButtonDefault
      class="flex-0 flex items-center order-1 md:order-3 md:ml-auto cursor-pointer md:min-w-fit h-[42px] border-0"
      @click="toggleHeaderMenu"
      :icon="!headerMenuOpen ? 'ChevronDownIcon' : 'ChevronUpIcon'"
      iconPos="right"
      size="small"
      severity="secondary"
      outlined>
      <div class="flex items-center">
        <span class="sr-only">User-Menü öffnen</span>

        <div class="flex flex-col items-center">
          <img
            v-if="avatarUrl"
            :class="{ 'size-5': $titrop.user.roles.developer || $titrop.user.roles.admin || $titrop.user.roles.management || $titrop.user.roles.beta }"
            :src="avatarUrl"
            :title="'Titrop-Id: #' + $titrop.user.current.id"
            alt=""
            class="flex-1 rounded-full bg-gray-50 size-8" />
          <div
            v-if="false && ($titrop.user.roles.developer || $titrop.user.roles.admin || $titrop.user.roles.management || $titrop.user.roles.beta)"
            class="flex-1 inline-flex flex-row h-3">
            <RoleDevIcon
              v-if="$titrop.user.roles.developer"
              :size="14"
              title="Entwickler" />
            <RoleAdminIcon
              v-if="$titrop.user.roles.admin"
              :size="14"
              title="Administrator" />
            <RoleManagementIcon
              v-if="$titrop.user.roles.management"
              :size="14"
              title="Verwaltung" />
            <RoleBetaIcon
              v-if="$titrop.user.roles.beta"
              :size="14"
              title="Beta-Tester" />
          </div>
        </div>
        <span
          aria-hidden="true"
          class="flex-1 mx-4 text-sm font-semibold leading-6 text-gray-700 dark:text-gray-100 text-nowrap">
        <span v-if="titrop.showIds">[{{ $titrop.user.current.id }}]</span>
        {{ $titrop.user.current.displayname }}
      </span>
        <!--      <ChevronDownIcon
                :class="{ 'rotate-180': headerMenuOpen }"
                aria-hidden="true"
                class="flex-1 h-5 w-5 min-w-5 text-gray-400 dark:text-gray-600" />-->
      </div>
    </ButtonDefault>
    <Menu
      ref="headerMenu"
      :model="headerMenuItems"
      :popup="true"
      @hide="onMenuHide">
      <template #item="{ item }">
        <div class="items-center">
          <ButtonDefault
            size="small"
            variant="text"
            severity="secondary"
            class="text-gray-700 w-full justify-start"
            :class="[item.routeOrFunc.meta?.markAsAdmin ? markAsAdminClasses : '',
                       item.routeOrFunc.meta?.markAsBeta ? markAsBetaClasses : '',
                       item.routeOrFunc.meta?.markAsDev ? markAsDevClasses : '',
                       item.routeOrFunc.path === currentRoute ? 'router-link-active' : ''
                       ]"
            :label="item.label as string"
            :icon="item.icon as AllIconsType"
            @click="test(item as MenuItem)" />
          <!--          <button
                      v-if="typeof item.routeOrFunc == 'function'"
                      v-ripple
                      class="inline-flex w-full py-2 px-2 titrop-button-secondary border-0 items-center"
                      @click="item.routeOrFunc">
                      <component :is="item.icon" class="mr-2 size-6" />
                      {{ item.label }}
                    </button>
                    <RouterLink
                      v-else
                      v-ripple
                      :class="[item.routeOrFunc.meta?.markAsAdmin ? markAsAdminClasses : '', item.routeOrFunc.meta?.markAsBeta ? markAsBetaClasses : '', item.routeOrFunc.meta?.markAsDev ? markAsDevClasses : '']"
                      :to="item.routeOrFunc"
                      :icon="item.icon"
                      class="inline-flex w-full py-2 px-2 titrop-button-secondary border-0 items-center">
                      <component :is="item.icon" class="mr-2 size-6" />
                      {{ item.label }}
                    </RouterLink>-->
        </div>
      </template>
      <template #end>
        <div class="flex align-items-center w-full px-0 py-2">
          <ButtonDefault
            class="w-full text-xs"
            label="Alle Caches löschen"
            severity="help"
            @click="clearStoreCaches"
            outlined
            size="small"
            icon="DeleteIcon" />
        </div>
        <div
          v-if="$user.roles.developer"
          class="text-xs font-bold bg-blue-200 dark:bg-gray-800 flex align-items-center w-full px-0 py-2"
          @dblclick="useTitropEvents().hotkeyToggleDeveloperDialog.emit()">
          (Dev-Options sind in Strg+Alt+D verschoben)
        </div>
      </template>
    </Menu>

    <div class="order-3 md:order-4 ml-auto md:pl-1 flex items-center gap-x-2 md:gap-x-4 md:pr-0">
      <!-- Separator -->
      <div
        aria-hidden="true"
        class="hidden md:block mx-0 h-6 md:w-px bg-slate-200 dark:bg-slate-400/30" />
      <NotificationHeaderComponent />
      <button
        v-if="$titrop.config.app('enable_activity_feed', false).value"
        class="titrop-button-secondary"
        type="button"
        @click="toggleNotifications">
        <span class="sr-only">Aktivitätsfeed</span>
        <BellIcon
          aria-hidden="true"
          class="h-6 w-6" />
      </button>

      <!--      <button class="titrop-button-secondary" v-if="false">
              <BrightnessDark
                v-if="isDarkMode"
                :title="`Modus: Dunkel`"
                @click="nextStyle()" />
              <BrightnessLight
                v-else-if="isLightMode"
                :title="`Modus: Hell`"
                @click="nextStyle()" />
              <BrightnessAuto
                v-else-if="isAutoMode"
                :title="`Modus: Automatisch`"
                @click="nextStyle()" />
            </button>
            <button v-if="$user.roles.admin && false"
                    class="titrop-button-secondary"
                    type="button"
                    @click="toggleMetric">
              <span class="sr-only">Metrik</span>
              <RoleDevIcon class="h-6 w-6" />
            </button>-->

      <NavigationMobile class="md:hidden" />
    </div>
    <HeaderSearch class="order-5 md:order-2 basis-full" />
  </div>
</template>
